import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Instagram, Facebook, Linkedin } from 'lucide-react';
import { socialMediaClients } from '../../mock';

const BoldSocialMediaSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section id="social-media" className="py-32 px-6 relative">
      <div className="section-watermark" style={{ top: '5%', right: '-5%' }}>SM</div>
      
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.h2
          className="text-center mb-24 gradient-text"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          Social Media
        </motion.h2>
        
        {/* Alternating Left/Right Layout - Same as Projects */}
        <div className="space-y-32">
          {socialMediaClients.map((client, index) => {
            const isEven = index % 2 === 0;
            
            return (
              <motion.div
                key={client.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                }`}
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Image/Icon Side */}
                <motion.div
                  className={`${isEven ? '' : 'lg:col-start-2'} relative group`}
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="glass-card p-16 flex items-center justify-center relative overflow-hidden min-h-[400px]">
                    {/* Large Instagram Icon */}
                    <motion.div
                      className="relative z-10"
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Instagram 
                        size={120} 
                        className="text-[#C026D3]" 
                        strokeWidth={1.5}
                      />
                    </motion.div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-6 left-6">
                      <span className="px-5 py-2 bg-[#0E0B1F]/90 backdrop-blur-sm text-[#FF5C39] rounded-full text-sm font-bold border border-[#FF5C39]/50">
                        {client.category}
                      </span>
                    </div>
                    
                    {/* Gradient Overlay */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/20 via-transparent to-[#C026D3]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  </div>
                </motion.div>
                
                {/* Content Side */}
                <motion.div
                  className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}
                  initial={{ opacity: 0, x: isEven ? 60 : -60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <h3 className="text-4xl md:text-5xl font-bold text-[#EDE9FE] mb-8" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
                    {client.name}
                  </h3>
                  
                  {/* Challenge / Approach / Outcome */}
                  <div className="space-y-6 mb-8">
                    <div>
                      <span className="text-sm text-[#7C3AED] font-bold uppercase tracking-wider">Challenge</span>
                      <p className="text-xl text-[#EDE9FE] mt-2">{client.challenge}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm text-[#C026D3] font-bold uppercase tracking-wider">Approach</span>
                      <p className="text-xl text-[#EDE9FE] mt-2">{client.approach}</p>
                    </div>
                    
                    <motion.div
                      whileHover={{ x: 5 }}
                    >
                      <span className="text-sm text-[#22D3A5] font-bold uppercase tracking-wider">Outcome</span>
                      <p className="text-xl text-[#22D3A5] font-bold mt-2">
                        {client.outcome}
                      </p>
                    </motion.div>
                  </div>
                  
                  {/* Platform Links */}
                  <div className="flex items-center gap-4">
                    <motion.a
                      href={client.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#C026D3] flex items-center justify-center text-white transition-all duration-300 hover:shadow-2xl hover:shadow-[#7C3AED]/50"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Instagram"
                    >
                      <Instagram size={24} />
                    </motion.a>
                    
                    <motion.a
                      href={client.facebook}
                      target={client.facebook !== '#' ? '_blank' : undefined}
                      rel={client.facebook !== '#' ? 'noopener noreferrer' : undefined}
                      className={`w-14 h-14 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#C026D3] flex items-center justify-center text-white transition-all duration-300 hover:shadow-2xl hover:shadow-[#7C3AED]/50 ${
                        client.facebook === '#' ? 'opacity-40 cursor-not-allowed' : ''
                      }`}
                      whileHover={client.facebook !== '#' ? { scale: 1.1, rotate: 5 } : {}}
                      whileTap={client.facebook !== '#' ? { scale: 0.95 } : {}}
                      aria-label="Facebook"
                      onClick={(e) => {
                        if (client.facebook === '#') {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Facebook size={24} />
                    </motion.a>
                    
                    <motion.a
                      href={client.linkedin}
                      target={client.linkedin !== '#' ? '_blank' : undefined}
                      rel={client.linkedin !== '#' ? 'noopener noreferrer' : undefined}
                      className={`w-14 h-14 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#C026D3] flex items-center justify-center text-white transition-all duration-300 hover:shadow-2xl hover:shadow-[#7C3AED]/50 ${
                        client.linkedin === '#' ? 'opacity-40 cursor-not-allowed' : ''
                      }`}
                      whileHover={client.linkedin !== '#' ? { scale: 1.1, rotate: 5 } : {}}
                      whileTap={client.linkedin !== '#' ? { scale: 0.95 } : {}}
                      aria-label="LinkedIn"
                      onClick={(e) => {
                        if (client.linkedin === '#') {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Linkedin size={24} />
                    </motion.a>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BoldSocialMediaSection;
